import { TestType, TestCardStatus } from "@app/provider/testData/types";
import { CounselingIntent } from "@app/utils/gisEntrySource";

export type PublicGisLookupIdentifier = "zipCode" | "mobileNumber" | "email";

/**
 * Demographics collected by the GIS order-lookup stepper. Mirrors the public
 * Match Patient stepper data but is owned by the GIS funnel so the two flows
 * stay decoupled.
 */
export interface PublicGisLookupData {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  mobileNumber: string;
  email: string;
  zipCode?: string;
  alternativeLastName?: string;
}

/**
 * GuestMPErrors-style terminal outcomes for the order-lookup funnel. A
 * multi-order match is NOT terminal — the funnel shows the order picker
 * (`PublicGisLookupScreen.ORDER_SELECT`) instead.
 */
export type PublicGisLookupErrorType =
  | "MP_NO_PATIENT_MATCH"
  | "MP_NO_ORDER_FOUND"
  | "MP_NO_MATCHING_ORDER"
  | "MP_MULTIPLE_ORDERS"
  | "MP_ACTIVE_ALREADY_EXISTS"
  | "MP_GENERIC_NO_MATCH_ERROR";

/**
 * A GIS-eligible order resolved by `matchGisOrdersNonAuth`. Each carries its
 * own order-bound guest `token`, so selecting an order yields a token already
 * authorized for the downstream NonAuth GIS + Acuity surface.
 */
export interface PublicGisMatchedOrder {
  orderUidPublic: string;
  testUid: string;
  testType: TestType | string;
  /**
   * Raw SOS sales status. Not classifiable into pre/post-test on its own — a
   * released Signatera result reports `TNP` — so use `counselingIntent`.
   */
  status?: TestCardStatus | string;
  /** Server-resolved pre/post-test classification for this order. */
  counselingIntent?: CounselingIntent;
  /**
   * LIMS/SOS result code, used to resolve result-specific Acuity appointment
   * types (Empower negative/carrier, Panorama low-risk).
   */
  resultCode?: string;
  createdAt?: string;
  limsCaseId?: number;
  token: string;
}

/** Screens within the single-route order-lookup funnel. */
export enum PublicGisLookupScreen {
  STEPPER = "STEPPER",
  MATCHING = "MATCHING",
  ORDER_SELECT = "ORDER_SELECT",
  WH_OH_SCHEDULE = "WH_OH_SCHEDULE",
  ONC_INTAKE = "ONC_INTAKE",
  ERROR = "ERROR",
}
